/**
 * ImgScroll Module — WebP Image Sequence with CSS Zoom/Pan
 *
 * Scroll-driven animation using pre-rendered WebP states. Each step shows
 * a different image, optionally zoomed/panned via CSS transform. Crossfade
 * between images is driven by scroll progress (not time-based).
 *
 * Architecture:
 *   - Fixed fullscreen overlay with 2 stacked <img> elements
 *   - Images at full natural size (CSS px), transform for contain + zoom
 *   - GPU texture at full resolution (will-change: transform permanent)
 *   - Crossfade: new image fades in ON TOP (z-index swap), then old fades out
 *   - decode() before showing new image (no pop)
 *   - Generation counter against stale decode() callbacks
 *
 * Data Attributes (Container panel):
 *   data-imgscroll             Mode flag (activates this module)
 *   data-is-image="url"        First image URL
 *   data-is-natural="W,H"     Natural dimensions (width,height in px)
 *
 * Data Attributes (Step panels):
 *   data-is-step="N"           Step number (1-based)
 *   data-is-image="url"        Image URL for this step
 *   data-is-focus="x,y,s"     Focus: x%, y%, scale (empty = overview)
 *   data-is-mobile-focus="x,y,s"  Mobile override (optional, <=768px)
 *   data-is-transition="2.0"  Transition duration in seconds (for reference)
 *
 * Events: Listens to story:panel:activated
 * Registration: core.registerModule('imgscroll', this)
 *
 * File: assets/js/story/story-module-imgscroll.js
 */

(function (window) {
  'use strict';

  // ---- Debug logging ----
  var DEBUG = false;
  function log() {
    if (DEBUG && window.console) {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[ImgScroll]');
      console.log.apply(console, args);
    }
  }
  if (window.location && window.location.search.indexOf('debug=story') !== -1) {
    DEBUG = true;
  }


  /* ========================================
     Constructor
     ======================================== */

  function StoryImgScroll(core) {
    this.core = core;
    this.overlay = null;
    this.front = null;
    this.back = null;
    this.containerPanel = null;
    this.containerIndex = -1;
    this.naturalW = 0;
    this.naturalH = 0;
    this.isMobile = window.innerWidth <= 768;

    // Step sequence: [{panelIndex, image, focus, transition}]
    this.steps = [];
    this.currentStep = -1;
    this.liveFocus = [50, 50, 1]; // current visual state [x%, y%, scale]

    // Animation state
    this.animGen = 0;
    this.animId = null;
    this.fadeOutId = null;

    // Track previous panel for reverse Hub detection
    this.prevActivatedIndex = -1;

    // Preloaded images
    this._preloaded = {};

    // Bound handlers
    this._onPanelActivated = this.handlePanelActivated.bind(this);
    this._onResize = null;

    this.init();
  }


  /* ========================================
     Init
     ======================================== */

  StoryImgScroll.prototype.init = function () {
    if (!this.core || !this.core.root) return;

    var root = this.core.root;

    // 1. Find container panel (has data-imgscroll)
    var container = root.querySelector('[data-imgscroll]');
    if (!container) {
      log('No [data-imgscroll] found, module inactive');
      return;
    }

    this.containerPanel = this.findPanel(container);
    if (!this.containerPanel) {
      log('Container not inside a panel, aborting');
      return;
    }

    log('Container found');

    // 2. Parse natural dimensions (mobile override if available)
    var naturalStr = (this.isMobile && container.dataset.isMobileNatural)
      ? container.dataset.isMobileNatural
      : (container.dataset.isNatural || '');
    var parts = naturalStr.split(',');
    this.naturalW = parseFloat(parts[0]) || 1;
    this.naturalH = parseFloat(parts[1]) || 1;

    // Parse fade-out duration (ms) from data-is-fadeout, default 600
    this.fadeOutDur = parseFloat(container.dataset.isFadeout) || 600;
    log('Natural:', this.naturalW, 'x', this.naturalH);

    // 3. Create overlay DOM
    this.createOverlay();

    // 4. Build step sequence from panels
    this.buildSequence(root, container);

    // 5. Preload all images
    this.preloadImages();

    // 6. Register event listener
    document.addEventListener('story:panel:activated', this._onPanelActivated);

    // 7. Resize handler (debounced)
    var self = this;
    var resizeTimer = null;
    this._onResize = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        self.applyFrame(self.front, self.liveFocus);
        self.applyFrame(self.back, self.liveFocus);
      }, 100);
    };
    window.addEventListener('resize', this._onResize);

    // 8. Register with core
    this.core.registerModule('imgscroll', this);

    log('Module initialized,', this.steps.length, 'steps');
  };


  /* ========================================
     DOM: Find parent panel
     ======================================== */

  StoryImgScroll.prototype.findPanel = function (el) {
    var node = el;
    while (node) {
      if (node.classList && node.classList.contains('story-panel')) return node;
      node = node.parentElement;
    }
    return el; // fallback: element itself
  };


  /* ========================================
     DOM: Create fixed overlay
     ======================================== */

  StoryImgScroll.prototype.createOverlay = function () {
    var overlay = document.createElement('div');
    overlay.className = 'is-overlay';

    var back = document.createElement('img');
    back.className = 'is-layer';
    back.alt = '';
    back.style.opacity = '0';
    back.style.zIndex = '0';
    back.style.width = this.naturalW + 'px';
    back.style.height = this.naturalH + 'px';

    var front = document.createElement('img');
    front.className = 'is-layer';
    front.alt = '';
    front.style.opacity = '1';
    front.style.zIndex = '1';
    front.style.width = this.naturalW + 'px';
    front.style.height = this.naturalH + 'px';

    overlay.appendChild(back);
    overlay.appendChild(front);

    // Append to body (NOT inside panel — panels have transform which breaks position:fixed)
    document.body.appendChild(overlay);

    this.overlay = overlay;
    this.front = front;
    this.back = back;

    log('Overlay created');
  };


  /* ========================================
     Build step sequence from data attributes
     ======================================== */

  StoryImgScroll.prototype.buildSequence = function (root, container) {
    var allPanels = Array.prototype.slice.call(
      root.querySelectorAll('.story-panel')
    );
    var self = this;

    // Step 0: container itself
    var containerIdx = allPanels.indexOf(this.containerPanel);
    this.containerIndex = containerIdx;

    var containerFocusAttr = this.isMobile
      ? (container.dataset.isMobileFocus || container.dataset.isFocus)
      : container.dataset.isFocus;
    this.steps.push({
      panelIndex: containerIdx,
      image: this.mobileImageSwap(container.dataset.isImage || ''),
      focus: this.parseFocus(containerFocusAttr),
      transition: parseFloat(container.dataset.isTransition) || 2.0
    });

    // Steps 1..N: panels with data-is-step
    var stepPanels = Array.prototype.slice.call(
      root.querySelectorAll('[data-is-step]')
    );

    // Sort by step number
    stepPanels.sort(function (a, b) {
      return (parseInt(a.dataset.isStep, 10) || 0) - (parseInt(b.dataset.isStep, 10) || 0);
    });

    stepPanels.forEach(function (panel) {
      var stepPanel = self.findPanel(panel);
      var panelIdx = allPanels.indexOf(stepPanel);

      var focusAttr = self.isMobile
        ? (panel.dataset.isMobileFocus || panel.dataset.isFocus)
        : panel.dataset.isFocus;

      self.steps.push({
        panelIndex: panelIdx,
        image: self.mobileImageSwap(panel.dataset.isImage || ''),
        focus: self.parseFocus(focusAttr),
        transition: parseFloat(panel.dataset.isTransition) || 2.0
      });
    });

    log('Steps built:', this.steps.length);
  };


  /* ========================================
     Parse focus string "x,y,scale" → [x, y, s] or null
     ======================================== */

  StoryImgScroll.prototype.parseFocus = function (str) {
    if (!str || !str.trim()) return null;
    var p = str.split(',');
    if (p.length < 3) return null;
    return [parseFloat(p[0]), parseFloat(p[1]), parseFloat(p[2])];
  };


  /* ========================================
     Mobile image swap: _o.webp → .webp
     Smaller versions avoid iOS 4096px GPU limit
     ======================================== */

  StoryImgScroll.prototype.mobileImageSwap = function (url) {
    if (!this.isMobile) return url;
    if (url.indexOf('_o.webp') !== -1) {
      var swapped = url.replace('_o.webp', '.webp');
      log('Mobile swap:', url.split('/').pop(), '→', swapped.split('/').pop());
      return swapped;
    }
    return url;
  };


  /* ========================================
     Focus helpers
     ======================================== */

  StoryImgScroll.prototype.focusOrDefault = function (f) {
    return f ? [f[0], f[1], f[2]] : [50, 50, 1];
  };


  /* ========================================
     Transform: contain + zoom + pan
     ======================================== */

  StoryImgScroll.prototype.calcTransform = function (focus) {
    if (!this.overlay) return 'none';
    var vpW = this.overlay.clientWidth;
    var vpH = this.overlay.clientHeight;

    // Contain scale: fit naturalW x naturalH into viewport
    var containScale = Math.min(vpW / this.naturalW, vpH / this.naturalH);

    // Total = contain * zoom (zoom 1 = overview)
    var totalScale = containScale * focus[2];

    // Focus point in image pixels
    var fxPx = this.naturalW * focus[0] / 100;
    var fyPx = this.naturalH * focus[1] / 100;

    // Center focus point in viewport
    var tx = vpW / 2 - fxPx * totalScale;
    var ty = vpH / 2 - fyPx * totalScale;

    return 'translate(' + tx + 'px, ' + ty + 'px) scale(' + totalScale + ')';
  };

  StoryImgScroll.prototype.applyFrame = function (imgEl, focus) {
    if (!imgEl) return;
    imgEl.style.transition = 'none';
    imgEl.style.transform = this.calcTransform(focus);
  };


  /* ========================================
     Preload images
     ======================================== */

  StoryImgScroll.prototype.preloadImages = function () {
    var self = this;
    this.steps.forEach(function (step) {
      if (step.image && !self._preloaded[step.image]) {
        var img = new Image();
        img.src = step.image;
        self._preloaded[step.image] = img;
      }
    });
    log('Preloading', Object.keys(this._preloaded).length, 'images');
  };


  /* ========================================
     Animation engine
     ======================================== */

  StoryImgScroll.prototype.stopFadeOut = function () {
    if (this.fadeOutId) {
      cancelAnimationFrame(this.fadeOutId);
      this.fadeOutId = null;
    }
  };

  StoryImgScroll.prototype.stopAnim = function () {
    this.animGen++;
    this.stopFadeOut();
    if (this.animId) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
    // Mid-crossfade? Snap to completion
    if (this.back.style.zIndex === '2') {
      this.back.style.opacity = '1';
      var temp = this.front;
      this.front = this.back;
      this.back = temp;
      this.front.style.zIndex = '1';
      this.back.style.zIndex = '0';
    }
  };

  // Easing functions
  var EASINGS = {
    'linear':      function (t) { return t; },
    'ease-in-out': function (t) { return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2; }
  };

  StoryImgScroll.prototype.goToStep = function (idx, animate) {
    if (idx < 0 || idx >= this.steps.length) return;
    if (idx === this.currentStep) return;

    this.stopAnim();

    var step = this.steps[idx];
    var prevStep = this.currentStep >= 0 ? this.steps[this.currentStep] : null;
    var imageChanges = !prevStep || (step.image !== prevStep.image);
    var toFocus = this.focusOrDefault(step.focus);
    var self = this;

    if (animate && imageChanges) {
      // Animated crossfade with zoom/pan interpolation
      var fromFocus = this.liveFocus.slice();
      var dur = step.transition || 2.0;
      var easeFn = EASINGS['ease-in-out'];

      // Setup: new image on top, hidden
      this.back.style.transition = 'none';
      this.back.style.opacity = '0';
      this.back.style.zIndex = '2';
      this.back.src = step.image;
      this.applyFrame(this.back, fromFocus);

      var myGen = this.animGen;
      var front = this.front;
      var back = this.back;

      back.decode().then(function () {
        if (myGen !== self.animGen) return;

        var startTime = null;
        function tick(now) {
          if (!startTime) startTime = now;
          var progress = Math.min((now - startTime) / (dur * 1000), 1);
          var t = easeFn(progress);

          var curFocus = [
            fromFocus[0] + (toFocus[0] - fromFocus[0]) * t,
            fromFocus[1] + (toFocus[1] - fromFocus[1]) * t,
            fromFocus[2] + (toFocus[2] - fromFocus[2]) * t
          ];

          var tf = self.calcTransform(curFocus);
          front.style.transform = tf;
          back.style.transform = tf;
          back.style.opacity = String(t);
          self.liveFocus = curFocus.slice();

          if (progress < 1) {
            self.animId = requestAnimationFrame(tick);
          } else {
            self.animId = null;

            // Swap roles
            var temp = self.front;
            self.front = self.back;
            self.back = temp;
            self.front.style.zIndex = '1';
            self.back.style.zIndex = '0';

            // Phase 2: fade out old layer
            var fadeOutDur = self.fadeOutDur;
            var fadeStart = null;
            function fadeOld(now2) {
              if (!fadeStart) fadeStart = now2;
              var p = Math.min((now2 - fadeStart) / fadeOutDur, 1);
              self.back.style.opacity = String(1 - p);
              if (p < 1) {
                self.fadeOutId = requestAnimationFrame(fadeOld);
              } else {
                self.fadeOutId = null;
              }
            }
            self.fadeOutId = requestAnimationFrame(fadeOld);

            self.liveFocus = toFocus.slice();
          }
        }
        self.animId = requestAnimationFrame(tick);

      }).catch(function () {
        if (myGen !== self.animGen) return;
        // Fallback: short CSS crossfade instead of hard cut
        self.front.src = step.image;
        self.front.style.transition = 'opacity 0.3s ease';
        self.front.style.opacity = '1';
        self.back.style.transition = 'opacity 0.3s ease';
        self.back.style.opacity = '0';
        self.back.style.zIndex = '0';
        self.liveFocus = toFocus.slice();
        self.applyFrame(self.front, self.liveFocus);
        // Clean up transition after done
        setTimeout(function () {
          self.front.style.transition = '';
          self.back.style.transition = '';
        }, 350);
      });

    } else if (animate && !imageChanges) {
      // Same image, just zoom/pan interpolation
      var fromFocus2 = this.liveFocus.slice();
      var dur2 = step.transition || 2.0;
      var easeFn2 = EASINGS['ease-in-out'];
      var startTime2 = null;

      function tick2(now) {
        if (!startTime2) startTime2 = now;
        var progress = Math.min((now - startTime2) / (dur2 * 1000), 1);
        var t = easeFn2(progress);

        var curFocus = [
          fromFocus2[0] + (toFocus[0] - fromFocus2[0]) * t,
          fromFocus2[1] + (toFocus[1] - fromFocus2[1]) * t,
          fromFocus2[2] + (toFocus[2] - fromFocus2[2]) * t
        ];

        self.front.style.transform = self.calcTransform(curFocus);
        self.liveFocus = curFocus.slice();

        if (progress < 1) {
          self.animId = requestAnimationFrame(tick2);
        } else {
          self.animId = null;
          self.liveFocus = toFocus.slice();
        }
      }
      this.animId = requestAnimationFrame(tick2);

    } else {
      // Instant (no animation)
      if (imageChanges) {
        var temp = this.front;
        this.front = this.back;
        this.back = temp;
        this.front.src = step.image;
        this.front.style.opacity = '1';
        this.front.style.zIndex = '1';
        this.back.style.zIndex = '0';
      }
      this.liveFocus = toFocus.slice();
      this.applyFrame(this.front, this.liveFocus);
      this.applyFrame(this.back, this.liveFocus);
    }

    this.currentStep = idx;
    log('Step', idx, imageChanges ? '(image change)' : '(zoom only)');
  };


  /* ========================================
     Panel activation handler
     ======================================== */

  StoryImgScroll.prototype.handlePanelActivated = function (e) {
    var d = e.detail || {};
    var activatedIndex = typeof d.panelIndex === 'number' ? d.panelIndex : d.index;
    if (typeof activatedIndex !== 'number') return;

    // Show overlay when any imgscroll panel is active
    var stepIdx = this.findStepForPanel(activatedIndex);

    // Always hide overlay pins when leaving overlay-hub (any panel change)
    var isOverlayHub = false;
    if (stepIdx < 0) {
      var allPanels = this.core.root.querySelectorAll('.story-panel');
      var activatedPanel = allPanels[activatedIndex];
      if (activatedPanel && activatedPanel.hasAttribute('data-hub')
          && activatedPanel.dataset.hubMode === 'overlay' && window.innerWidth > 768) {
        isOverlayHub = true;
      }
    }
    if (this._overlayHubActive && !isOverlayHub) {
      this._exitOverlayHub();
    }

    if (stepIdx >= 0) {
      // Show overlay
      if (!this.overlay.classList.contains('is-active')) {
        // Check if we're coming FROM a Hub panel (reverse transition)
        var allPanels2 = this.core.root.querySelectorAll('.story-panel');
        var prevPanel = this.prevActivatedIndex >= 0 ? allPanels2[this.prevActivatedIndex] : null;

        if (prevPanel && prevPanel.hasAttribute('data-hub')
            && prevPanel.dataset.hubMode !== 'overlay') {
          // Standard Hub: reverse crossfade with double-rAF
          var self2 = this;
          this.overlay.style.transition = 'none';
          this.overlay.style.opacity = '0';
          this.overlay.classList.add('is-active');

          requestAnimationFrame(function () {
            self2.overlay.style.transition = 'opacity 1s ease';
            requestAnimationFrame(function () {
              self2.overlay.style.opacity = '1';
              log('Reverse Hub fade started (1s)');
            });
          });

          setTimeout(function () {
            self2.overlay.style.transition = '';
            self2.overlay.style.opacity = '';
          }, 1100);
          log('Overlay shown (reverse Hub fade)');
        } else if (!prevPanel || !prevPanel.hasAttribute('data-hub')) {
          // Not coming from any Hub — simple show
          this.overlay.classList.add('is-active');
          log('Overlay shown');
        }
        // Overlay-Hub: overlay was never hidden, no fade needed
      }

      // Hide early overlay pins when scrolling back from last step
      if (stepIdx < this.steps.length - 1) {
        if (this._earlyPinTimer) {
          clearTimeout(this._earlyPinTimer);
          this._earlyPinTimer = null;
        }
        if (!this._overlayHubActive && this._overlayPinLayer
            && this._overlayPinLayer.classList.contains('is-visible')) {
          this._hideOverlayPins();
        }
      }

      // Navigate to step
      var animate = this.currentStep >= 0;
      this.goToStep(stepIdx, animate);

      // Show overlay pins early on last IS step — user sees hub overview
      // image and pins appear during the animation, no extra scroll needed.
      if (stepIdx === this.steps.length - 1 && !this._overlayHubActive) {
        var overlayHubPanel = this.core.root.querySelector('[data-hub-mode="overlay"]');
        if (overlayHubPanel) {
          if (this._earlyPinTimer) clearTimeout(this._earlyPinTimer);
          var self3 = this;
          this._earlyPinTimer = setTimeout(function () {
            self3._earlyPinTimer = null;
            if (!self3._overlayHubActive) {
              self3._lastHubPanel = overlayHubPanel;
              self3._showOverlayPins(overlayHubPanel);
            }
          }, 600);
        }
      }

    } else {
      // Not an imgscroll panel — check if it's a Hub
      var allPanels3 = allPanels || this.core.root.querySelectorAll('.story-panel');
      var activatedPanel2 = activatedPanel || allPanels3[activatedIndex];

      if (isOverlayHub) {
        // Overlay-Hub: re-activate overlay if it was hidden (e.g. after chapter nav)
        if (!this.overlay.classList.contains('is-active')) {
          this.overlay.classList.add('is-active');
          this.overlay.style.transition = 'none';
          this.overlay.style.opacity = '1';
          this.overlay.style.clip = '';
          // Clear inline overrides next frame — CSS .is-active handles opacity from here.
          // CRITICAL: inline opacity MUST be cleared, otherwise it overrides
          // CSS opacity:0 when is-active is later removed (overlay stays visible over chapters!)
          var ov = this.overlay;
          requestAnimationFrame(function () { ov.style.transition = ''; ov.style.opacity = ''; });
          log('Overlay re-activated for overlay-hub return');
        }
        this._enterOverlayHub(activatedPanel2);
      } else if (this.overlay.classList.contains('is-active')) {
        if (activatedPanel2 && activatedPanel2.hasAttribute('data-hub')) {
          this._fadeToHub(activatedPanel2);
        } else {
          this.overlay.classList.remove('is-active');
          this.stopAnim();
          log('Overlay hidden');
        }
      }
    }

    this.prevActivatedIndex = activatedIndex;
  };

  StoryImgScroll.prototype.findStepForPanel = function (panelIndex) {
    for (var i = 0; i < this.steps.length; i++) {
      if (this.steps[i].panelIndex === panelIndex) return i;
    }
    return -1;
  };


  /* ========================================
     Overlay-as-Hub — IS overlay stays visible, pins appear on top
     Activated by data-hub-mode="overlay" on the hub panel.
     Desktop only (mobile falls back to standard hub-mobile).
     ======================================== */

  StoryImgScroll.prototype._enterOverlayHub = function (hubPanel) {
    if (this._overlayHubActive) return;
    this._overlayHubActive = true;

    this.stopAnim();
    this.currentStep = -1;
    this._lastHubPanel = hubPanel;

    // Clear any early pin timer (pins may have been shown on last IS step)
    if (this._earlyPinTimer) {
      clearTimeout(this._earlyPinTimer);
      this._earlyPinTimer = null;
    }

    // Ensure front image has the hub overview image (last step = hub map).
    // IO may have skipped steps (fast scroll), so front might show an earlier image.
    var lastStep = this.steps.length > 0 ? this.steps[this.steps.length - 1] : null;
    var needsImageSwap = false;
    if (lastStep && lastStep.image) {
      var lastFile = lastStep.image.split('/').pop();
      var currentFile = this.front.src ? this.front.src.split('/').pop() : '';
      if (lastFile !== currentFile) {
        this.front.src = lastStep.image;
        needsImageSwap = true;
        log('Overlay-Hub: swapped to hub image:', lastFile, '(was:', currentFile + ')');
      }
    }
    this.front.style.opacity = '1';
    this.front.style.zIndex = '1';

    // If pins already visible (early display on last IS step), just finalize state
    if (this._overlayPinLayer && this._overlayPinLayer.classList.contains('is-visible')) {
      this.liveFocus = [50, 50, 1];
      this.front.style.transition = '';
      log('Overlay-Hub: entering (pins already visible from last step)');
      return;
    }

    // Animate IS-layer to overview position (zoom-out), then show pins
    var overviewTransform = this.calcTransform([50, 50, 1]);
    var self = this;
    var pinShown = false;

    function showPins() {
      if (pinShown) return;
      // Guard: only show if hub is still active (prevents stale timer firing)
      if (!self._overlayHubActive) return;
      pinShown = true;
      self._showPinTimeout = null;
      self.front.removeEventListener('transitionend', showPins);
      self._showOverlayPins(hubPanel);
      // Drop will-change for crisp Safari rendering (no animation while hub is static)
      self.front.style.willChange = 'auto';
    }

    // Check if already at overview (re-entry after chapter) — skip animation
    var currentTransform = this.front.style.transform;
    if (currentTransform === overviewTransform) {
      this.front.style.transition = '';
      showPins();
      log('Overlay-Hub: entering (already at overview, pins immediate)');
    } else {
      this.front.style.transition = 'transform 0.8s ease';
      this.front.style.transform = overviewTransform;
      // Show pins during zoom (overlap), not after — feels more responsive
      if (this._showPinTimeout) clearTimeout(this._showPinTimeout);
      this._showPinTimeout = setTimeout(showPins, 350);
      log('Overlay-Hub: entering (zoom-out to overview)');
    }

    this.liveFocus = [50, 50, 1];
  };


  StoryImgScroll.prototype._exitOverlayHub = function () {
    if (!this._overlayHubActive) return;
    this._overlayHubActive = false;

    if (this._earlyPinTimer) {
      clearTimeout(this._earlyPinTimer);
      this._earlyPinTimer = null;
    }
    if (this._showPinTimeout) {
      clearTimeout(this._showPinTimeout);
      this._showPinTimeout = null;
    }
    this._hideOverlayPins();
    // Restore will-change for smooth IS animation
    this.front.style.willChange = 'transform';
    // Reset transition so goToStep can set transform instantly
    this.front.style.transition = '';

    log('Overlay-Hub: exiting');
  };


  StoryImgScroll.prototype._showOverlayPins = function (hubPanel) {
    var self = this;

    // Lazy-create the overlay pin container
    if (!this._overlayPinLayer) {
      var layer = document.createElement('div');
      layer.className = 'hub-overlay-pins';
      document.body.appendChild(layer);
      this._overlayPinLayer = layer;

      // Resize handler — recalculate pin-layer geometry
      var resizeTimer;
      this._overlayPinResize = function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          self._resizeOverlayPins();
        }, 100);
      };
      window.addEventListener('resize', this._overlayPinResize);
    }

    // Move pins from hub panel into overlay layer (preserves event listeners)
    var pinLayer = hubPanel.querySelector('.hub-pin-layer');
    if (pinLayer) {
      var pins = pinLayer.querySelectorAll('.hub-pin');
      this._overlayPinSource = pinLayer; // remember origin for return
      for (var i = 0; i < pins.length; i++) {
        this._overlayPinLayer.appendChild(pins[i]);
      }
    }

    // Size and position the overlay pin layer
    this._resizeOverlayPins();

    // Fade in
    this._overlayPinLayer.classList.add('is-visible');

    log('Overlay-Hub: pins shown (' + (pinLayer ? pinLayer.querySelectorAll('.hub-pin').length : 0) + ' moved + ' + this._overlayPinLayer.querySelectorAll('.hub-pin').length + ' total)');
  };


  StoryImgScroll.prototype._hideOverlayPins = function () {
    if (!this._overlayPinLayer) return;

    // Hide immediately (no fade delay — fadeTo already handles visual transition)
    this._overlayPinLayer.classList.remove('is-visible');
    this._overlayPinLayer.style.opacity = '0';

    // Move pins back to hub-panel immediately so they're available for next show
    if (this._overlayPinSource) {
      var pins = this._overlayPinLayer.querySelectorAll('.hub-pin');
      for (var i = 0; i < pins.length; i++) {
        this._overlayPinSource.appendChild(pins[i]);
      }
    }

    // Reset opacity for next use
    var layer = this._overlayPinLayer;
    requestAnimationFrame(function () {
      layer.style.opacity = '';
    });

    log('Overlay-Hub: pins hidden');
  };


  StoryImgScroll.prototype._resizeOverlayPins = function () {
    if (!this._overlayPinLayer || !this.overlay) return;

    // Calculate contain geometry — same math as calcTransform([50,50,1])
    var vpW = this.overlay.clientWidth;
    var vpH = this.overlay.clientHeight;
    var containScale = Math.min(vpW / this.naturalW, vpH / this.naturalH);
    var renderedW = this.naturalW * containScale;
    var renderedH = this.naturalH * containScale;
    var left = (vpW - renderedW) / 2;
    var top = (vpH - renderedH) / 2;

    this._overlayPinLayer.style.width = renderedW + 'px';
    this._overlayPinLayer.style.height = renderedH + 'px';
    this._overlayPinLayer.style.left = left + 'px';
    this._overlayPinLayer.style.top = top + 'px';

    log('Overlay-Hub: pin layer sized', Math.round(renderedW) + 'x' + Math.round(renderedH), 'at', Math.round(left) + ',' + Math.round(top));
  };


  /* ========================================
     Hub Crossfade (Standard) — smooth transition from IS overlay to Hub panel
     Hub now uses <img class="hub-bg"> with object-fit:contain — same
     GPU compositing pipeline as the IS overlay. Both show the same
     image (vs-base-hub-1_o.webp) at contain-fit → the crossfade is
     a pure opacity transition between two identical <img> elements.
     No geometry matching needed.
     ======================================== */

  StoryImgScroll.prototype._fadeToHub = function (hubPanel) {
    // Guard against multiple parallel _fadeToHub calls
    // (multiple story:panel:activated events for the same hub)
    if (this._hubFading) {
      log('_fadeToHub already in progress, skipping');
      return;
    }
    this._hubFading = true;

    var self = this;

    this.stopAnim();

    // Reset currentStep so goToStep runs fully when scrolling back
    this.currentStep = -1;

    // Store Hub reference for reverse-fade cleanup
    this._lastHubPanel = hubPanel;

    log('Fading to Hub (waiting for snap)');

    // Wait for scroll-snap to settle before fading.
    // panel:activated fires early (IntersectionObserver, ~10% visible),
    // but we need the Hub fully in position.
    var maxWait = 120; // ~2s safety limit at 60fps
    var waited = 0;
    function waitForSnap() {
      var rect = hubPanel.getBoundingClientRect();
      waited++;
      if (rect.top > 2 && waited < maxWait) {
        requestAnimationFrame(waitForSnap);
        return;
      }

      log('Snap settled after ~' + Math.round(waited * 16.7) + 'ms (top=' + rect.top.toFixed(1) + ')');

      // Ensure hub-bg <img> is decoded and GPU-ready before fading.
      // decode() guarantees the image texture is uploaded to the GPU,
      // unlike new Image().onload which only guarantees download.
      var hubBg = hubPanel.querySelector('.hub-bg');
      var fadeStarted = false;

      function safeFade() {
        if (fadeStarted) return;
        fadeStarted = true;
        doFade();
      }

      function doFade() {
        var hubRect = hubPanel.getBoundingClientRect();
        var frontImg = self.front;
        var overlayRect = self.overlay.getBoundingClientRect();

        // ── Debug Diagnostics ──────────────────────────────────
        // Activated by ?debug=story — measures all geometry before
        // and after the transform correction for comparison.
        if (DEBUG) {
          var hubBgEl = hubPanel.querySelector('.hub-bg');
          var hubBgRect = hubBgEl ? hubBgEl.getBoundingClientRect() : null;

          // IS-layer current transform (before correction)
          var isTransformBefore = frontImg ? frontImg.style.transform : 'none';

          // IS contain geometry (what calcTransform would compute)
          var isContainScale = Math.min(overlayRect.width / self.naturalW, overlayRect.height / self.naturalH);
          var isRenderedW = self.naturalW * isContainScale;
          var isRenderedH = self.naturalH * isContainScale;
          var isTx = (overlayRect.width - isRenderedW) / 2;
          var isTy = (overlayRect.height - isRenderedH) / 2;

          // Hub contain geometry (what object-fit:contain produces)
          var hubContainScale = Math.min(hubRect.width / self.naturalW, hubRect.height / self.naturalH);
          var hubRenderedW = self.naturalW * hubContainScale;
          var hubRenderedH = self.naturalH * hubContainScale;
          var hubCalcTx = (hubRect.width - hubRenderedW) / 2;
          var hubCalcTy = (hubRect.height - hubRenderedH) / 2;

          // Hub-bg actual rendered area (from browser's object-fit)
          var hubBgActual = null;
          if (hubBgEl) {
            var cs = window.getComputedStyle(hubBgEl);
            hubBgActual = {
              objectFit: cs.objectFit,
              objectPosition: cs.objectPosition,
              cssWidth: cs.width,
              cssHeight: cs.height
            };
          }

          // Deltas
          var deltaW = overlayRect.width - hubRect.width;
          var deltaScale = isContainScale - hubContainScale;
          var deltaRenderedW = isRenderedW - hubRenderedW;
          var deltaRenderedH = isRenderedH - hubRenderedH;

          console.group('%c[ImgScroll] Hub Crossfade Diagnostics', 'color: #ff6b35; font-weight: bold; font-size: 14px');

          console.log('%cImage Natural Size', 'font-weight:bold',
            self.naturalW + ' × ' + self.naturalH,
            '(ratio: ' + (self.naturalW / self.naturalH).toFixed(4) + ')');

          console.table({
            'Overlay (IS)': {
              width: overlayRect.width.toFixed(1),
              height: overlayRect.height.toFixed(1),
              top: overlayRect.top.toFixed(1),
              left: overlayRect.left.toFixed(1),
              containScale: isContainScale.toFixed(6),
              renderedW: isRenderedW.toFixed(1),
              renderedH: isRenderedH.toFixed(1),
              tx: isTx.toFixed(2),
              ty: isTy.toFixed(2)
            },
            'Hub Panel': {
              width: hubRect.width.toFixed(1),
              height: hubRect.height.toFixed(1),
              top: hubRect.top.toFixed(1),
              left: hubRect.left.toFixed(1),
              containScale: hubContainScale.toFixed(6),
              renderedW: hubRenderedW.toFixed(1),
              renderedH: hubRenderedH.toFixed(1),
              tx: hubCalcTx.toFixed(2),
              ty: hubCalcTy.toFixed(2)
            },
            'Delta': {
              width: deltaW.toFixed(1),
              height: (overlayRect.height - hubRect.height).toFixed(1),
              top: (overlayRect.top - hubRect.top).toFixed(1),
              left: (overlayRect.left - hubRect.left).toFixed(1),
              containScale: deltaScale.toFixed(6),
              renderedW: deltaRenderedW.toFixed(1),
              renderedH: deltaRenderedH.toFixed(1),
              tx: (isTx - hubCalcTx).toFixed(2),
              ty: (isTy - hubCalcTy).toFixed(2)
            }
          });

          if (hubBgActual) {
            console.log('%cHub-bg CSS', 'font-weight:bold', hubBgActual);
          }
          if (hubBgRect) {
            console.log('%cHub-bg BCR', 'font-weight:bold',
              Math.round(hubBgRect.width) + '×' + Math.round(hubBgRect.height),
              'at (' + hubBgRect.left.toFixed(1) + ', ' + hubBgRect.top.toFixed(1) + ')');
          }

          console.log('%cIS transform (before)', 'font-weight:bold', isTransformBefore);
          console.log('%cClip rect', 'font-weight:bold',
            'top=' + hubRect.top.toFixed(1) +
            ' right=' + hubRect.right.toFixed(1) +
            ' bottom=' + hubRect.bottom.toFixed(1) +
            ' left=' + hubRect.left.toFixed(1));
          console.log('%cWidth-limited?', 'font-weight:bold',
            (overlayRect.width / self.naturalW) < (overlayRect.height / self.naturalH) ? 'YES (width)' : 'NO (height)');
          console.groupEnd();
        }
        // ── End Debug ──────────────────────────────────────────

        // Reposition IS-layer to match hub-bg exactly.
        // IS overlay is wider than hub panel (sidebar), so calcTransform()
        // produces a different contain-scale/position than hub-bg's
        // object-fit:contain. Compute the hub-bg geometry directly and
        // set the IS front image to that exact transform.
        if (frontImg && self.naturalW && self.naturalH) {
          var hubScale = Math.min(hubRect.width / self.naturalW, hubRect.height / self.naturalH);
          var hubTx = (hubRect.width - self.naturalW * hubScale) / 2;
          var hubTy = (hubRect.height - self.naturalH * hubScale) / 2;
          frontImg.style.transform = 'translate(' + hubTx.toFixed(4) + 'px, ' + hubTy.toFixed(4) + 'px) scale(' + hubScale.toFixed(6) + ')';
          log('IS-layer repositioned: tx=' + hubTx.toFixed(1) + ' ty=' + hubTy.toFixed(1) + ' scale=' + hubScale.toFixed(4));

          if (DEBUG) {
            console.log('%c[ImgScroll] IS transform (after)', 'color: #ff6b35; font-weight:bold',
              frontImg.style.transform);
          }
        }

        // Clip overlay to Hub panel bounds (in case of sidebars)
        self.overlay.style.clip = 'rect(' +
          hubRect.top + 'px,' +
          hubRect.right + 'px,' +
          hubRect.bottom + 'px,' +
          hubRect.left + 'px)';

        // Double-rAF to fully override CSS .is-overlay transition (0.4s).
        // Transform fix + transition:none happen in the same sync block
        // → browser batches into one paint, no visible jump.
        self.overlay.style.transition = 'none';
        self.overlay.style.opacity = '1';
        self.overlay.classList.remove('is-active');

        requestAnimationFrame(function () {
          self.overlay.style.transition = 'opacity 1s ease';
          requestAnimationFrame(function () {
            self.overlay.style.opacity = '0';
            log('Hub fade started (1s)');
          });
        });

        // Clean up after fade completes.
        setTimeout(function () {
          self.overlay.style.transition = '';
          self.overlay.style.opacity = '';
          self.overlay.style.clip = '';
          self._hubFading = false;
        }, 1200);
      }

      if (hubBg && typeof hubBg.decode === 'function') {
        hubBg.decode().then(function () {
          log('Hub bg decoded (GPU ready), starting fade');
          safeFade();
        }).catch(safeFade);
        setTimeout(safeFade, 2000); // safety timeout
      } else {
        safeFade();
      }
    }
    requestAnimationFrame(waitForSnap);
  };


  /* ========================================
     Destroy (cleanup)
     ======================================== */

  StoryImgScroll.prototype.destroy = function () {
    this.stopAnim();
    document.removeEventListener('story:panel:activated', this._onPanelActivated);
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
  };


  /* ========================================
     Expose globally
     ======================================== */

  window.StoryModuleImgScroll = StoryImgScroll;

})(window);
